import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

interface DialogueTextBoxProps {
  text?: string;
  children?: React.ReactNode;
  actionLabel: string;
  onAction: () => void;
  onClose: () => void;
  actionDisabled?: boolean;
  /** Optional avatar element rendered above the top-right corner of the box */
  avatar?: React.ReactNode;
  /** Tailwind border-color class for the top accent (e.g. "border-amber-500") */
  accentBorderClass?: string;
  /** Tailwind gradient classes for the action button */
  actionButtonClass?: string;
}

export const DialogueTextBox: React.FC<DialogueTextBoxProps> = ({
  text,
  children,
  actionLabel,
  onAction,
  onClose,
  actionDisabled = false,
  avatar,
  accentBorderClass = 'border-amber-500',
  actionButtonClass = 'bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 hover:from-yellow-400 hover:via-amber-300 hover:to-yellow-400 text-black',
}) => {
  return createPortal(
    <div className="fixed dialogue-text-box-position left-0 right-0 lg:max-w-[675px] lg:mx-auto z-[2147483001] animate-slide-up">
      {avatar && (
        <div className="flex justify-end pr-4">
          {avatar}
        </div>
      )}
      <div
        className={cn(
          'relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl border-t-2',
          accentBorderClass,
          'px-5 pb-5 pt-4'
        )}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X size={14} />
        </button>

        {/* Dialogue content — scrollable */}
        <div className="max-h-[40vh] overflow-y-auto mb-4">
          {children ?? (
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
              {text}
            </p>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mb-2 leading-tight">
          *This avatar analyst provides an objective perspective based on a specific
          investment philosophy. It is for educational purposes only and does not
          constitute financial advice.
        </p>

        {/* Action button */}
        <button
          onClick={onAction}
          disabled={actionDisabled}
          className={cn(
            'w-full rounded-xl font-bold py-3 px-6 transition-colors',
            actionDisabled && 'opacity-50 cursor-not-allowed',
            actionButtonClass
          )}
        >
          {actionLabel}
        </button>
      </div>
    </div>,
    document.body
  );
};
